import { parseWistiaMediaId, parseWistiaStartTime } from "./source.js";
import { EMPTY_REMOTE, EMPTY_TEXT_TRACKS } from "@videojs/media";
import { createTimeRange } from "@videojs/media/dom";
//#region src/normalize.ts
/**
* Wistia's spellings for the media events it renames, against the ones a media element uses. `play`, `pause`, `ended`,
* `seeking`, and `seeked` are absent because Wistia already spells those the way a media does.
*
* `durationchange` comes from three, because Wistia has no single moment where a duration appears: `duration` reads `0`
* until `api-ready`, the media data lands on `loaded-media-data`, and `loaded-metadata` may wait on the viewer
* interacting. `loadedmetadata` is held to once per source below, since that one is not repeatable. `timeupdate` comes
* from two: `second-change` is a coarser subset of `time-update`, so taking both keeps the clock running at whatever
* resolution is on offer, and a repeat costs one re-read of the playhead. `progress` and `playing` ride along because
* Wistia announces neither.
*
* @internal
*/
const WISTIA_EVENT_ALIASES = {
	"api-ready": ["loadedmetadata", "durationchange"],
	"can-play": ["canplay"],
	"can-play-through": ["canplaythrough"],
	"loaded-data": ["loadeddata"],
	"loaded-media-data": ["durationchange", "contentdatachange"],
	"loaded-metadata": ["loadedmetadata", "durationchange"],
	"load-start": ["loadstart"],
	play: ["playing"],
	"rate-change": ["ratechange"],
	"second-change": ["timeupdate", "progress"],
	"time-update": ["timeupdate", "progress"],
	"volume-change": ["volumechange"]
};
/**
* Give a `<wistia-player>` the surface the rest of Video.js expects of a media: the members `HTMLMediaElement` has that
* Wistia names differently or not at all, and the events it spells its own way.
*
* Applied to the element rather than wrapped around it, so the player a consumer holds and the media the store drives
* are one object — `WistiaAdapter` calls this on itself, React on the element it renders. Idempotent.
*
* `controls` is deliberately not among the members: Wistia has one already and it means the player's control instances,
* which its internals read. Turning chrome on and off is `wistiaControlProps`.
*
* @internal
*/
function normalizeWistiaPlayer(player) {
	const target = player;
	if (normalized.has(target)) return player;
	normalized.add(target);
	const endSeek = () => {
		const state = stateOf(target);
		if (!state.seeking) return;
		state.seeking = false;
		target.dispatchEvent(new Event("seeked"));
	};
	target.addEventListener("seeking", () => {
		stateOf(target).seeking = true;
	});
	target.addEventListener("seeked", () => {
		stateOf(target).seeking = false;
	});
	for (const type of ["second-change", "time-update"]) target.addEventListener(type, endSeek);
	for (const [from, to] of Object.entries(WISTIA_EVENT_ALIASES)) target.addEventListener(from, () => {
		for (const type of to) {
			if (type === "loadedmetadata") {
				const state = stateOf(target);
				if (state.announcedMetadata) continue;
				state.announcedMetadata = true;
			}
			target.dispatchEvent(new Event(type));
		}
	});
	Object.defineProperties(target, WISTIA_MEDIA_DESCRIPTORS);
	return player;
}
const normalized = /* @__PURE__ */ new WeakSet();
/** The source last assigned to a player, which is the record of what a consumer asked for. */
const sources = /* @__PURE__ */ new WeakMap();
/** Values with no home on the player, held per element rather than on it. */
const shadowState = /* @__PURE__ */ new WeakMap();
function stateOf(player) {
	let state = shadowState.get(player);
	if (!state) shadowState.set(player, state = {
		announcedMetadata: false,
		defaultMuted: false,
		playsInline: true,
		seeking: false
	});
	return state;
}
/**
* An empty range that is not the shared `EMPTY_TIME_RANGES`: `isMediaBufferCapable` reads that exact object as "no
* buffer surface at all" and skips the feature for good, where a Wistia player has one and merely has nothing in it
* until a duration arrives.
*/
const NO_RANGE = Object.freeze({
	length: 0,
	start: () => 0,
	end: () => 0
});
function accessor(get, set) {
	return {
		configurable: true,
		enumerable: true,
		get,
		...set && { set }
	};
}
/** Always reports this, and swallows what the store writes back: Wistia has nowhere to put it. */
function inert(value) {
	return accessor(() => value, () => {});
}
function method(value) {
	return {
		configurable: true,
		value
	};
}
const WISTIA_MEDIA_DESCRIPTORS = {
	/** The media's hashed id. Accepts any Wistia URL too, and reports the id it resolved to. */
	src: accessor(function() {
		return this.mediaId ?? "";
	}, function(value) {
		this.source = {
			...this.source,
			mediaId: parseWistiaMediaId(value) ?? ""
		};
		const startTime = parseWistiaStartTime(value);
		if (startTime != null) this.currentTime = startTime;
	}),
	currentSrc: accessor(function() {
		return this.mediaId ?? "";
	}),
	/** Wistia's own options, `mediaId` among them. Assigning applies every one of them to the player. */
	source: accessor(function() {
		return sources.get(this) ?? (this.mediaId ? { mediaId: this.mediaId } : null);
	}, function(value) {
		sources.set(this, value);
		stateOf(this).announcedMetadata = false;
		this.dispatchEvent(new Event("emptied"));
		if (value) Object.assign(this, value);
		else this.mediaId = "";
		this.dispatchEvent(new Event("sourcechange"));
	}),
	/**
	* True unless the media is playing. Wistia answers `state === 'paused'`, which is `false` in the `beforeplay` state a
	* player opens in and `false` again before its API is ready — so left alone, the first click on an untouched player
	* pauses something that was never playing.
	*/
	paused: accessor(function() {
		return this.state !== "playing";
	}),
	/** Wistia spells looping as an end-of-video behavior. */
	loop: accessor(function() {
		return this.endVideoBehavior === "loop";
	}, function(value) {
		this.endVideoBehavior = value ? "loop" : "default";
	}),
	/** The player is live from the moment it exists, so this mutes it now and `muted` changes it afterwards. */
	defaultMuted: accessor(function() {
		return stateOf(this).defaultMuted;
	}, function(value) {
		stateOf(this).defaultMuted = value;
		this.muted = value;
	}),
	/** Stored and reported, but never passed on: Wistia has no inline-playback knob and plays inline. */
	playsInline: accessor(function() {
		return stateOf(this).playsInline;
	}, function(value) {
		stateOf(this).playsInline = value;
	}),
	/** Whether a seek is in flight: tracked from events, and defined at all because the store gates seeking on it. */
	seeking: accessor(function() {
		return stateOf(this).seeking;
	}),
	/** Wistia reports no seekable range of its own, and every Wistia media is seekable end to end. */
	seekable: accessor(function() {
		const duration = this.duration;
		return duration > 0 && Number.isFinite(duration) ? createTimeRange(0, duration) : NO_RANGE;
	}),
	/** The media's own metadata. Wistia knows its name once `loaded-media-data` has fired, and nothing else. */
	contentData: accessor(function() {
		return { title: this.name ?? null };
	}),
	played: accessor(() => NO_RANGE),
	textTracks: accessor(() => EMPTY_TEXT_TRACKS),
	videoWidth: accessor(() => 0),
	videoHeight: accessor(() => 0),
	error: accessor(() => null),
	crossOrigin: inert(null),
	remote: accessor(() => EMPTY_REMOTE),
	isPictureInPicture: accessor(() => false),
	disableRemotePlayback: inert(true),
	disablePictureInPicture: inert(true),
	/** Wistia serves on-demand media only. */
	streamType: inert("on-demand"),
	isFullscreen: accessor(function() {
		return this.inFullscreen === true;
	}),
	exitFullscreen: method(function() {
		return this.cancelFullscreen();
	}),
	/** Re-apply the current source, which is all a Wistia player can be asked to reload. */
	load: method(function() {
		const source = this.source;
		this.source = source;
	}),
	canPlayType: method(() => ""),
	addTextTrack: method(() => void 0)
};
//#endregion
export { WISTIA_EVENT_ALIASES, normalizeWistiaPlayer };
